import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  ArrowLeft, 
  ArrowRight, 
  FileText, 
  Briefcase, 
  Building2, 
  Upload, 
  Linkedin, 
  CheckCircle, 
  Sparkles, 
  Target,
  Settings,
  ChevronDown,
  BarChart3,
  GraduationCap,
  Heart,
  Users,
  X,
  Paperclip
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { DocumentUpload } from "@/components/DocumentUpload";
import { JobWizard } from "@/components/JobWizard/JobWizard";
import { supabase } from "@/integrations/supabase/client";

type WizardStep = 'source' | 'resume' | 'documents' | 'job-details' | 'additional' | 'advanced' | 'processing' | 'results';
type TailoringSource = 'job-description' | 'job-position' | 'institution';
type ResumeSourceType = 'existing' | 'upload' | 'paste' | 'linkedin';
type JobSourceType = 'upload' | 'existing';

interface WizardData {
  // Basic settings
  tailoringSource: TailoringSource | null;
  generateAnalysis: boolean;
  
  // Resume data
  resumeSourceType: ResumeSourceType | null;
  selectedResumeId: string | null;
  pastedResumeText: string;
  uploadedResumeFile: File | null;
  processedResumeData: string;
  
  // Documents
  selectedDocuments: string[];
  
  // Job data
  jobSourceType: JobSourceType | null;
  selectedJobId: string | null;
  jobDescription: string;
  jobTitle: string;
  company: string;
  institutionName: string;
  institutionType: string;
  department: string;
  
  // Additional options
  additionalInstructions: string;
  additionalDocuments: Array<{ file: File; extractedText: string }>;
  
  // Advanced options
  showAdvancedOptions: boolean;
  mentionCompanyName: boolean;
  mentionCompanyMission: boolean;
  mentionEducation: boolean;
  updateLinkedIn: boolean;
  workExperienceFramework: string;
  transferableSkillsFocus: boolean;
  skillRelevanceOptimization: boolean;
  irrelevantJobFiltering: boolean;
}

export default function ResumeWizard() {
  const [isOpen, setIsOpen] = useState(true);
  const [currentStep, setCurrentStep] = useState<WizardStep>('source');
  const [wizardData, setWizardData] = useState<WizardData>({
    tailoringSource: null,
    generateAnalysis: true,
    resumeSourceType: null,
    selectedResumeId: null,
    pastedResumeText: '',
    uploadedResumeFile: null,
    processedResumeData: '',
    selectedDocuments: [],
    jobSourceType: null,
    selectedJobId: null,
    jobDescription: '',
    jobTitle: '',
    company: '',
    institutionName: '',
    institutionType: '',
    department: '',
    additionalInstructions: '',
    additionalDocuments: [],
    showAdvancedOptions: false,
    mentionCompanyName: false,
    mentionCompanyMission: false,
    mentionEducation: false,
    updateLinkedIn: false,
    workExperienceFramework: 'star',
    transferableSkillsFocus: false,
    skillRelevanceOptimization: false,
    irrelevantJobFiltering: false
  });
  
  const [userResumes, setUserResumes] = useState<any[]>([]);
  const [savedJobs, setSavedJobs] = useState<any[]>([]);
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [showJobWizard, setShowJobWizard] = useState(false);
  const [processingResume, setProcessingResume] = useState(false);
  
  const { toast } = useToast();
  const { user } = useAuth();

  const steps: { key: WizardStep; title: string; description: string }[] = [
    { key: 'source', title: 'Choose Source', description: 'Select what you want to tailor for' },
    { key: 'resume', title: 'Resume Source', description: 'Provide your resume' },
    { key: 'documents', title: 'Select Documents', description: 'Choose documents to tailor' },
    { key: 'job-details', title: 'Job Details', description: 'Provide job or institution details' },
    { key: 'additional', title: 'Additional Options', description: 'Extra instructions and documents' },
    { key: 'advanced', title: 'Advanced Settings', description: 'Fine-tune your preferences' },
    { key: 'processing', title: 'Processing', description: 'AI is tailoring your documents' },
    { key: 'results', title: 'Results', description: 'View your tailored documents' }
  ];

  const currentStepIndex = steps.findIndex(step => step.key === currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  // Load user resumes and jobs on mount
  useEffect(() => {
    if (user?.id) {
      loadUserResumes();
      loadSavedJobs();
    }
  }, [user?.id]);

  const loadUserResumes = async () => {
    setLoadingResumes(true);
    try {
      if (!user?.id) return;
      
      const { data: resumes, error } = await supabase
        .from('resumes')
        .select('id, title, updated_at, content')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setUserResumes(resumes || []);
    } catch (error) {
      console.error('Error loading resumes:', error);
    } finally {
      setLoadingResumes(false);
    }
  };

  const loadSavedJobs = async () => {
    setLoadingJobs(true);
    try {
      if (!user?.id) return;
      
      const { data: jobs, error } = await supabase
        .from('saved_job_descriptions')
        .select('id, job_title, company_name, full_job_description, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSavedJobs(jobs || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const updateWizardData = (updates: Partial<WizardData>) => {
    setWizardData(prev => ({ ...prev, ...updates }));
  };

  const handleResumeUpload = async (content: string, filename: string) => {
    setProcessingResume(true);
    try {
      updateWizardData({ processedResumeData: content });
      toast({ title: "Resume processed successfully!" });
    } catch (error) {
      toast({ title: "Error processing resume", variant: "destructive" });
    } finally {
      setProcessingResume(false);
    }
  };

  const handleDocumentToggle = (documentId: string) => {
    const isSelected = wizardData.selectedDocuments.includes(documentId);
    if (isSelected) {
      updateWizardData({
        selectedDocuments: wizardData.selectedDocuments.filter(id => id !== documentId)
      });
    } else {
      updateWizardData({
        selectedDocuments: [...wizardData.selectedDocuments, documentId]
      });
    }
  };

  const handleNext = () => {
    // Validation logic for each step
    if (currentStep === 'source') {
      if (!wizardData.tailoringSource) {
        toast({ title: "Please select a tailoring source", variant: "destructive" });
        return;
      }
      setCurrentStep('resume');
    } else if (currentStep === 'resume') {
      if (!wizardData.resumeSourceType) {
        toast({ title: "Please select a resume source", variant: "destructive" });
        return;
      }
      if (wizardData.resumeSourceType === 'existing' && !wizardData.selectedResumeId) {
        toast({ title: "Please select a resume", variant: "destructive" });
        return;
      }
      if (wizardData.resumeSourceType === 'paste' && !wizardData.pastedResumeText.trim()) {
        toast({ title: "Please paste your resume content", variant: "destructive" });
        return;
      }
      if (wizardData.resumeSourceType === 'upload' && !wizardData.uploadedResumeFile && !wizardData.processedResumeData) {
        toast({ title: "Please upload a resume file", variant: "destructive" });
        return;
      }
      setCurrentStep('documents');
    } else if (currentStep === 'documents') {
      if (wizardData.selectedDocuments.length === 0) {
        toast({ title: "Please select at least one document", variant: "destructive" });
        return;
      }
      setCurrentStep('job-details');
    } else if (currentStep === 'job-details') {
      // Validate based on source type
      if (wizardData.tailoringSource === 'job-description') {
        if (!wizardData.jobSourceType) {
          toast({ title: "Please select how to provide job details", variant: "destructive" });
          return;
        }
        if (wizardData.jobSourceType === 'existing' && !wizardData.selectedJobId) {
          toast({ title: "Please select a saved job", variant: "destructive" });
          return;
        }
        if (wizardData.jobSourceType === 'upload' && !wizardData.jobDescription.trim()) {
          toast({ title: "Please create a job description using the Job Wizard", variant: "destructive" });
          return;
        }
      }
      if (wizardData.tailoringSource === 'job-position' && (!wizardData.jobTitle.trim() || !wizardData.company.trim())) {
        toast({ title: "Please enter job title and company", variant: "destructive" });
        return;
      }
      if (wizardData.tailoringSource === 'institution' && !wizardData.institutionName.trim()) {
        toast({ title: "Please enter institution name", variant: "destructive" });
        return;
      }
      setCurrentStep('additional');
    } else if (currentStep === 'additional') {
      setCurrentStep('advanced');
    } else if (currentStep === 'advanced') {
      setCurrentStep('processing');
      // Simulate processing and then complete
      setTimeout(() => {
        setCurrentStep('results');
        toast({ 
          title: "Documents successfully tailored!", 
          description: `${wizardData.selectedDocuments.length} documents have been customized and are ready for use.`
        });
      }, 3000);
    }
  };

  const handleBack = () => {
    if (currentStep === 'resume') setCurrentStep('source');
    else if (currentStep === 'documents') setCurrentStep('resume');
    else if (currentStep === 'job-details') setCurrentStep('documents');
    else if (currentStep === 'additional') setCurrentStep('job-details');
    else if (currentStep === 'advanced') setCurrentStep('additional');
    else if (currentStep === 'processing') setCurrentStep('advanced');
    else if (currentStep === 'results') setCurrentStep('advanced');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'source':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">What would you like to tailor your documents for?</h3>
              <p className="text-muted-foreground">Choose the type of opportunity you're applying to</p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="generate-analysis-switch" className="text-base font-semibold">
                    Generate Match Score & Analysis
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Compare before and after tailoring with detailed analysis
                  </p>
                </div>
                <Switch 
                  id="generate-analysis-switch" 
                  checked={wizardData.generateAnalysis} 
                  onCheckedChange={(checked) => updateWizardData({ generateAnalysis: checked })} 
                />
              </div>
            </div>
            
            <div className="grid gap-4">
              {[
                {
                  id: 'job-description',
                  icon: FileText,
                  title: 'Job Description',
                  description: 'I have a specific job posting or description to tailor my documents for'
                },
                {
                  id: 'job-position',
                  icon: Briefcase,
                  title: 'Job Position',
                  description: 'I know the job title and company but don\'t have the full description'
                },
                {
                  id: 'institution',
                  icon: Building2,
                  title: 'Institution/Company',
                  description: 'I want to tailor my documents for a specific organization or institution'
                }
              ].map((option) => {
                const Icon = option.icon;
                const isSelected = wizardData.tailoringSource === option.id;
                return (
                  <Card 
                    key={option.id}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      isSelected ? 'border-primary bg-primary/5' : 'hover:border-primary/40'
                    }`}
                    onClick={() => updateWizardData({ tailoringSource: option.id as TailoringSource })}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold mb-1">{option.title}</h4>
                          <p className="text-sm text-muted-foreground">{option.description}</p>
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-primary" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );

      case 'resume':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">How would you like to provide your resume?</h3>
              <p className="text-muted-foreground">Choose the method that works best for you</p>
            </div>
            
            <RadioGroup 
              value={wizardData.resumeSourceType || ''} 
              onValueChange={(value) => updateWizardData({ resumeSourceType: value as ResumeSourceType })}
            >
              <div className="space-y-4">
                {/* Existing Resume */}
                <div className="flex items-center space-x-4 p-4 rounded-xl border hover:border-primary/50 hover:bg-muted/50 transition-all duration-200 cursor-pointer group">
                  <RadioGroupItem value="existing" id="existing" className="mt-0.5" />
                  <Label htmlFor="existing" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium">Choose Existing Resume</div>
                      <div className="text-sm text-muted-foreground">Select from saved resumes in your library</div>
                    </div>
                  </Label>
                </div>

                {wizardData.resumeSourceType === 'existing' && (
                  <div className="ml-12 mt-4 space-y-3">
                    <Label className="text-sm font-medium">Select Resume</Label>
                    {loadingResumes ? (
                      <div className="flex items-center gap-2 p-4 border rounded-lg">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-muted-foreground">Loading your resumes...</span>
                      </div>
                    ) : (
                      <Select 
                        value={wizardData.selectedResumeId || ''} 
                        onValueChange={(value) => updateWizardData({ selectedResumeId: value })}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Choose from your saved resumes" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover">
                          {userResumes.length > 0 ? userResumes.map(resume => (
                            <SelectItem key={resume.id} value={resume.id}>
                              {resume.title || 'Untitled Resume'} - {new Date(resume.updated_at).toLocaleDateString()}
                            </SelectItem>
                          )) : (
                            <SelectItem value="no-resumes" disabled>
                              No saved resumes found
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {/* Upload Resume */}
                <div className="flex items-center space-x-4 p-4 rounded-xl border hover:border-primary/50 hover:bg-muted/50 transition-all duration-200 cursor-pointer group">
                  <RadioGroupItem value="upload" id="upload" className="mt-0.5" />
                  <Label htmlFor="upload" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                      <Upload className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium">Upload New Resume</div>
                      <div className="text-sm text-muted-foreground">Accepts PDF, DOC, DOCX, TXT (max 10MB)</div>
                    </div>
                  </Label>
                </div>

                {wizardData.resumeSourceType === 'upload' && (
                  <div className="ml-12 mt-4">
                    <DocumentUpload 
                      onUploadComplete={handleResumeUpload} 
                      title="Upload Resume" 
                      description="Upload a PDF, Word document, or paste your resume text" 
                      acceptedTypes={['.pdf', '.doc', '.docx']} 
                    />
                  </div>
                )}

                {/* Paste Resume */}
                <div className="flex items-center space-x-4 p-4 rounded-xl border hover:border-primary/50 hover:bg-muted/50 transition-all duration-200 cursor-pointer group">
                  <RadioGroupItem value="paste" id="paste" className="mt-0.5" />
                  <Label htmlFor="paste" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium">Paste Resume Text</div>
                      <div className="text-sm text-muted-foreground">Copy and paste your resume content</div>
                    </div>
                  </Label>
                </div>

                {wizardData.resumeSourceType === 'paste' && (
                  <div className="ml-12 mt-4 space-y-4">
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Paste Resume Content</Label>
                      <Textarea 
                        placeholder="Paste your resume text here..." 
                        className="min-h-[200px]" 
                        value={wizardData.pastedResumeText} 
                        onChange={(e) => {
                          const content = e.target.value;
                          updateWizardData({ pastedResumeText: content });
                          if (content.trim().length > 100) {
                            handleResumeUpload(content, 'pasted-resume.txt');
                          }
                        }} 
                      />
                      {wizardData.pastedResumeText && wizardData.pastedResumeText.length > 100 && (
                        <div className="text-sm text-muted-foreground">
                          {wizardData.pastedResumeText.length} characters • Processing automatically...
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* LinkedIn Import */}
                <div className="flex items-center space-x-4 p-4 rounded-xl border opacity-50 cursor-not-allowed">
                  <RadioGroupItem value="linkedin" id="linkedin" className="mt-0.5" disabled />
                  <Label htmlFor="linkedin" className="flex items-center gap-3 cursor-not-allowed flex-1">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                      <Linkedin className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium">Import LinkedIn Profile</div>
                      <div className="text-sm text-muted-foreground">Import from LinkedIn - Coming soon</div>
                    </div>
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </div>
        );

      case 'documents':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Select Documents to Tailor</h3>
              <p className="text-muted-foreground">Choose which documents you want to create or update</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { id: 'resume', label: 'Resume', icon: FileText, description: 'Primary application document' },
                { id: 'cover-letter', label: 'Cover Letter', icon: FileText, description: 'Personalized introduction letter' },
                { id: 'thank-you-letter', label: 'Thank You Letter', icon: FileText, description: 'Post-interview follow-up' },
                { id: 'outreach-email', label: 'Outreach Email', icon: FileText, description: 'Networking introduction' },
                { id: 'executive-bio', label: 'Executive Bio', icon: FileText, description: 'Professional summary bio' },
                { id: 'linkedin-profile', label: 'LinkedIn Profile', icon: Linkedin, description: 'Social media presence' },
                { id: 'solution-asset-document', label: 'Solution Asset Document', icon: FileText, description: 'Strategic solution presentation' },
                { id: 'hiring-manager-contact-report', label: 'Hiring Manager Contact Report', icon: FileText, description: 'Decision-maker contact strategy' },
                { id: 'career-clarity-report', label: 'Career Clarity Report', icon: FileText, description: 'Career direction analysis' }
              ].map(document => {
                const Icon = document.icon;
                const isSelected = wizardData.selectedDocuments.includes(document.id);
                return (
                  <div 
                    key={document.id}
                    className={`group p-4 rounded-xl border transition-all duration-300 cursor-pointer hover:shadow-md ${
                      isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30'
                    }`}
                    onClick={() => handleDocumentToggle(document.id)}
                  >
                    <div className="flex items-start space-x-3">
                      <Checkbox 
                        id={document.id} 
                        checked={isSelected} 
                        onCheckedChange={() => handleDocumentToggle(document.id)}
                        className="mt-1 data-[state=checked]:bg-primary data-[state=checked]:border-primary" 
                      />
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 transition-colors ${isSelected ? 'text-primary' : 'text-muted-foreground group-hover:text-primary'}`} />
                          <Label htmlFor={document.id} className={`font-medium cursor-pointer transition-colors ${isSelected ? 'text-primary' : 'text-foreground group-hover:text-primary'}`}>
                            {document.label}
                          </Label>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {document.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {wizardData.selectedDocuments.length > 0 && (
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex items-center gap-2 text-sm text-primary">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">
                    {wizardData.selectedDocuments.length} document{wizardData.selectedDocuments.length > 1 ? 's' : ''} selected for tailoring
                  </span>
                </div>
              </div>
            )}
          </div>
        );

      case 'job-details':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">
                {wizardData.tailoringSource === 'job-description' && 'Provide Job Description'}
                {wizardData.tailoringSource === 'job-position' && 'Enter Job Details'}
                {wizardData.tailoringSource === 'institution' && 'Enter Institution Details'}
              </h3>
              <p className="text-muted-foreground">Provide the details we need to tailor your documents</p>
            </div>
            
            {wizardData.tailoringSource === 'job-description' && (
              <div className="space-y-6">
                <RadioGroup 
                  value={wizardData.jobSourceType || ''} 
                  onValueChange={(value) => updateWizardData({ jobSourceType: value as JobSourceType })}
                >
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4 p-4 rounded-xl border hover:border-primary/50 hover:bg-muted/50 transition-all duration-200 cursor-pointer group">
                      <RadioGroupItem value="upload" id="upload-job" className="mt-0.5" />
                      <Label htmlFor="upload-job" className="flex items-center gap-3 cursor-pointer flex-1">
                        <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                          <Upload className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-medium">Upload New Job</div>
                          <div className="text-sm text-muted-foreground">Upload a job description file or add via Job Wizard</div>
                        </div>
                      </Label>
                    </div>

                    <div className="flex items-center space-x-4 p-4 rounded-xl border hover:border-primary/50 hover:bg-muted/50 transition-all duration-200 cursor-pointer group">
                      <RadioGroupItem value="existing" id="existing-job" className="mt-0.5" />
                      <Label htmlFor="existing-job" className="flex items-center gap-3 cursor-pointer flex-1">
                        <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                          <FileText className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-medium">Use Existing Job</div>
                          <div className="text-sm text-muted-foreground">Select from your saved job descriptions</div>
                        </div>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>

                {wizardData.jobSourceType === 'upload' && (
                  <div className="ml-8 space-y-4 p-4 bg-muted/20 rounded-lg border">
                    <div className="text-sm font-medium">Create New Job Description</div>
                    <div className="text-center space-y-4">
                      <div className="p-6 border-2 border-dashed border-primary/25 rounded-lg bg-primary/5">
                        <Target className="w-12 h-12 mx-auto mb-4 text-primary" />
                        <h3 className="text-lg font-semibold text-primary mb-2">Job Wizard</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Use our guided wizard to create a comprehensive job description from a URL or by pasting the job posting
                        </p>
                        <Button onClick={() => setShowJobWizard(true)} className="bg-primary hover:bg-primary/90">
                          <Target className="w-4 h-4 mr-2" />
                          Open Job Wizard
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {wizardData.jobSourceType === 'existing' && (
                  <div className="ml-8 space-y-4 p-4 bg-muted/20 rounded-lg border">
                    <div className="text-sm font-medium">Select Job Description</div>
                    {loadingJobs ? (
                      <div className="flex items-center gap-2 p-4 border rounded-lg">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-muted-foreground">Loading your saved jobs...</span>
                      </div>
                    ) : savedJobs.length > 0 ? (
                      <div className="space-y-2">
                        <Select 
                          value={wizardData.selectedJobId || ''} 
                          onValueChange={(value) => {
                            updateWizardData({ selectedJobId: value });
                            // Auto-populate job description from selected job
                            const selectedJob = savedJobs.find(job => job.id === value);
                            if (selectedJob) {
                              updateWizardData({ 
                                jobDescription: selectedJob.full_job_description || '',
                                jobTitle: selectedJob.job_title || '',
                                company: selectedJob.company_name || ''
                              });
                            }
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Choose from your saved job descriptions" />
                          </SelectTrigger>
                          <SelectContent className="bg-popover">
                            {savedJobs.map(job => (
                              <SelectItem key={job.id} value={job.id}>
                                <div className="flex flex-col">
                                  <span className="font-medium">{job.job_title || 'Untitled Job'} at {job.company_name || 'Unknown Company'}</span>
                                  <span className="text-sm text-muted-foreground">
                                    Created {new Date(job.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {wizardData.selectedJobId && (
                          <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-green-800 dark:text-green-200">
                                Job selected and details loaded
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center p-8 border rounded-lg">
                        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-lg font-medium">No saved job descriptions found</p>
                        <p className="text-muted-foreground mb-4">Create your first job description using the Job Wizard</p>
                        <Button variant="outline" onClick={() => updateWizardData({ jobSourceType: 'upload' })}>
                          Upload New Job Instead
                        </Button>
                      </div>
                    )}
                  </div>
                )}

              </div>
            )}
            
            {wizardData.tailoringSource === 'job-position' && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="job-title">Job Title</Label>
                  <Input
                    id="job-title"
                    placeholder="e.g., Senior Software Engineer"
                    value={wizardData.jobTitle}
                    onChange={(e) => updateWizardData({ jobTitle: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="company">Company Name</Label>
                  <Input
                    id="company"
                    placeholder="e.g., Google, Microsoft, Apple"
                    value={wizardData.company}
                    onChange={(e) => updateWizardData({ company: e.target.value })}
                    className="mt-2"
                  />
                </div>
              </div>
            )}
            
            {wizardData.tailoringSource === 'institution' && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="institution-name">Institution Name</Label>
                  <Input
                    id="institution-name"
                    placeholder="e.g., Harvard University, Mayo Clinic"
                    value={wizardData.institutionName}
                    onChange={(e) => updateWizardData({ institutionName: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="institution-type">Institution Type (Optional)</Label>
                  <Input
                    id="institution-type"
                    placeholder="e.g., University, Hospital, Research Center"
                    value={wizardData.institutionType}
                    onChange={(e) => updateWizardData({ institutionType: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="department">Department (Optional)</Label>
                  <Input
                    id="department"
                    placeholder="e.g., Computer Science, Engineering"
                    value={wizardData.department}
                    onChange={(e) => updateWizardData({ department: e.target.value })}
                    className="mt-2"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 'additional':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Additional Instructions & Documents</h3>
              <p className="text-muted-foreground">Provide extra context to enhance your tailoring</p>
            </div>
            
            <div className="space-y-6">
              <div>
                <Label htmlFor="additional-instructions">Additional Instructions (Optional)</Label>
                <Textarea 
                  id="additional-instructions" 
                  placeholder="Include any specific guidelines, requirements, or preferences for the tailoring process..." 
                  value={wizardData.additionalInstructions} 
                  onChange={(e) => updateWizardData({ additionalInstructions: e.target.value })} 
                  rows={4} 
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-3 block">Upload Additional Documents (Optional)</Label>
                <DocumentUpload 
                  onUploadComplete={(content, filename) => {
                    const file = new File([content], filename, { type: 'text/plain' });
                    updateWizardData({
                      additionalDocuments: [...wizardData.additionalDocuments, { file, extractedText: content }]
                    });
                  }} 
                  title="Upload Additional Documents" 
                  description="Upload supporting documents, notes, or any additional context" 
                  acceptedTypes={['.pdf', '.doc', '.docx', '.txt']} 
                />
                
                {wizardData.additionalDocuments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <Label className="text-sm font-medium">Uploaded Documents ({wizardData.additionalDocuments.length}):</Label>
                    {wizardData.additionalDocuments.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-success" />
                          <span className="text-sm font-medium">{doc.file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({doc.extractedText.length} characters extracted)
                          </span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => {
                            const newDocs = wizardData.additionalDocuments.filter((_, i) => i !== index);
                            updateWizardData({ additionalDocuments: newDocs });
                          }} 
                          className="h-auto p-1 text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'advanced':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Advanced Tailoring Options</h3>
              <p className="text-muted-foreground">Fine-tune your resume tailoring preferences</p>
            </div>
            
            <div className="space-y-8">
              {/* Career Summary Options */}
              <section className="space-y-6">
                <div className="flex items-center gap-3 pb-3 border-b border-border">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                    <BarChart3 className="w-4 h-4 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Career Summary Enhancement</h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {[{
                    id: "mention-company",
                    checked: wizardData.mentionCompanyName,
                    title: "Company Name Integration",
                    description: "Naturally weave the target company name into your professional summary"
                  }, {
                    id: "mention-mission",
                    checked: wizardData.mentionCompanyMission,
                    title: "Mission Alignment",
                    description: "Reference the company's mission statement to show cultural fit"
                  }, {
                    id: "mention-education",
                    checked: wizardData.mentionEducation,
                    title: "Education Highlighting",
                    description: "Emphasize relevant education credentials in the summary"
                  }, {
                    id: "update-linkedin",
                    checked: wizardData.updateLinkedIn,
                    title: "LinkedIn Sync",
                    description: "Automatically update your LinkedIn profile with tailored content"
                  }].map(option => (
                    <div key={option.id} className="group p-4 rounded-xl border border-border bg-card hover:bg-muted/30 hover:border-primary/30 transition-all duration-300">
                      <div className="flex items-start space-x-3">
                        <Checkbox 
                          id={option.id} 
                          checked={option.checked} 
                          onCheckedChange={(checked) => {
                            updateWizardData({
                              [option.id === 'mention-company' ? 'mentionCompanyName' :
                               option.id === 'mention-mission' ? 'mentionCompanyMission' :
                               option.id === 'mention-education' ? 'mentionEducation' :
                               'updateLinkedIn']: checked === true
                            });
                          }} 
                          className="mt-1 data-[state=checked]:bg-primary data-[state=checked]:border-primary" 
                        />
                        <div className="space-y-2 flex-1">
                          <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                            {option.title}
                          </Label>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {option.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Professional Experience Framework */}
              <section className="space-y-6">
                <div className="flex items-center gap-3 pb-3 border-b border-border">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                    <Briefcase className="w-4 h-4 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Experience Formatting Framework</h3>
                </div>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Choose a proven framework to structure your experience bullet points for maximum impact
                  </p>
                  <RadioGroup 
                    value={wizardData.workExperienceFramework} 
                    onValueChange={(value) => updateWizardData({ workExperienceFramework: value })} 
                    className="space-y-3"
                  >
                    {[{
                      value: "star",
                      title: "STAR Method",
                      description: "Situation-Task-Action-Result format for compelling storytelling and measurable outcomes"
                    }, {
                      value: "par",
                      title: "PAR Method",
                      description: "Problem-Action-Result structure to showcase your problem-solving capabilities"
                    }, {
                      value: "arta",
                      title: "ARTA Method",
                      description: "Action-Result-Tools-Achievement framework emphasizing technical skills and tools"
                    }, {
                      value: "cosr",
                      title: "COSR Method",
                      description: "Challenge-Outcome-Skills-Results approach highlighting your effectiveness under pressure"
                    }, {
                      value: "none",
                      title: "Original Format",
                      description: "Preserve your existing bullet point structure without reformatting"
                    }].map(method => (
                      <div key={method.value} className="group p-4 rounded-xl border border-border bg-card hover:bg-muted/30 hover:border-primary/30 transition-all duration-300">
                        <div className="flex items-start space-x-3">
                          <RadioGroupItem value={method.value} id={method.value} className="mt-1 data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                          <div className="space-y-2 flex-1">
                            <Label htmlFor={method.value} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                              {method.title}
                            </Label>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {method.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </section>

              {/* Additional Experience Optimization */}
              <section className="space-y-6">
                <div className="flex items-center gap-3 pb-3 border-b border-border">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                    <Target className="w-4 h-4 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Content Optimization</h3>
                </div>
                <div className="space-y-4">
                  {[{
                    id: "transferable-skills",
                    checked: wizardData.transferableSkillsFocus,
                    title: "Transferable Skills Focus",
                    description: "Identify and highlight top 5 transferable hard skills and 3 key responsibilities that align with your target role"
                  }, {
                    id: "skill-relevance",
                    checked: wizardData.skillRelevanceOptimization,
                    title: "Skill Relevance Optimization",
                    description: "Remove bullet points that don't demonstrate transferable skills or target job requirements"
                  }, {
                    id: "irrelevant-filtering",
                    checked: wizardData.irrelevantJobFiltering,
                    title: "Irrelevant Experience Filtering",
                    description: "Minimize unrelated positions to 2 concise bullets while maintaining relevant qualifications"
                  }].map(option => (
                    <div key={option.id} className="group p-4 rounded-xl border border-border bg-card hover:bg-muted/30 hover:border-primary/30 transition-all duration-300">
                      <div className="flex items-start space-x-3">
                        <Checkbox 
                          id={option.id} 
                          checked={option.checked} 
                          onCheckedChange={(checked) => {
                            updateWizardData({
                              [option.id === 'transferable-skills' ? 'transferableSkillsFocus' :
                               option.id === 'skill-relevance' ? 'skillRelevanceOptimization' :
                               'irrelevantJobFiltering']: checked === true
                            });
                          }} 
                          className="mt-1 data-[state=checked]:bg-primary data-[state=checked]:border-primary" 
                        />
                        <div className="space-y-2 flex-1">
                          <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                            {option.title}
                          </Label>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {option.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        );

      case 'processing':
        return (
          <div className="space-y-6 text-center">
            <div className="space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Sparkles className="w-8 h-8 text-primary animate-pulse" />
              </div>
              <h3 className="text-lg font-semibold">Tailoring Your Documents</h3>
              <p className="text-muted-foreground">Our AI is analyzing and customizing your documents...</p>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 text-sm">
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
              </div>
              <p className="text-sm text-muted-foreground">This may take a few moments...</p>
            </div>
          </div>
        );

      case 'results':
        return (
          <div className="space-y-6 text-center">
            <div className="space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Documents Successfully Tailored!</h3>
              <p className="text-muted-foreground">Your {wizardData.selectedDocuments.length} documents have been customized and are ready for use.</p>
            </div>
            
            <div className="space-y-3">
              <Badge variant="secondary" className="px-4 py-2">
                {wizardData.selectedDocuments.length} Documents Ready
              </Badge>
              <div className="flex justify-center gap-4 mt-6">
                <Button onClick={() => setIsOpen(false)}>
                  View Documents
                </Button>
                <Button variant="outline" onClick={() => setCurrentStep('source')}>
                  Create Another
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center space-y-6">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold">Resume Wizard</h1>
            <p className="text-muted-foreground">Step-by-step document tailoring wizard</p>
          </div>
          
          <Button onClick={() => setIsOpen(true)} size="lg">
            <Sparkles className="w-5 h-5 mr-2" />
            Start Wizard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <div className="space-y-4">
              <DialogTitle className="text-2xl font-bold text-center">
                Resume Tailoring Wizard
              </DialogTitle>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{steps[currentStepIndex]?.title}</span>
                  <span className="text-muted-foreground">
                    Step {currentStepIndex + 1} of {steps.length}
                  </span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
              
              <p className="text-center text-muted-foreground">
                {steps[currentStepIndex]?.description}
              </p>
            </div>
          </DialogHeader>
          
          <div className="py-6">
            {renderStepContent()}
          </div>
          
          {currentStep !== 'processing' && currentStep !== 'results' && (
            <div className="flex justify-between pt-6 border-t">
              <Button 
                variant="outline" 
                onClick={handleBack}
                disabled={currentStep === 'source'}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              
              <Button onClick={handleNext}>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {showJobWizard && (
        <div className="fixed inset-0 z-[70] bg-black/20 overflow-y-auto">
          <JobWizard
            onClose={() => setShowJobWizard(false)}
            onSave={(jobData: any) => {
              setShowJobWizard(false);
              updateWizardData({ 
                jobDescription: jobData.fullJobDescription || jobData.description || '',
                jobTitle: jobData.jobTitle || jobData.title || '',
                company: jobData.companyName || jobData.company || ''
              });
              toast({ title: "Job description created and added!" });
              // Reload saved jobs to include the new one
              loadSavedJobs();
            }}
          />
        </div>
      )}
    </>
  );
}
